import { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store/store";
import { selectClusterList } from "../../store/slices/clusterListSlice";
import {
  increaseQuantity,
  decreaseQuantity,
  removeFromCart,
  incrementCutlery,
  decrementCutlery,
} from "../../store/slices/cartSlice";

export interface CartClusterGroup {
  clusterId: number;
  cluster: any;
  items: RootState["cart"]["items"];
  total: number;
}

export const useCart = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const cutleryByCluster = useSelector(
    (state: RootState) => state.cart.cutleryByCluster,
  );
  const clusters = useSelector(selectClusterList);

  const grouped: CartClusterGroup[] = useMemo(() => {
    const map = new Map<number, CartClusterGroup>();

    cartItems.forEach((item) => {
      if (!map.has(item.clusterId)) {
        const cluster = clusters.find((c) => c.id === item.clusterId);
        map.set(item.clusterId, {
          clusterId: item.clusterId,
          cluster,
          items: [],
          total: 0,
        });
      }
      const group = map.get(item.clusterId)!;
      group.items.push(item);
      group.total += item.price * item.quantity;
    });

    return Array.from(map.values());
  }, [cartItems, clusters]);

  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(
    grouped.length === 1 ? grouped[0].clusterId : null,
  );

  useEffect(() => {
    if (grouped.length === 1) {
      setSelectedClusterId(grouped[0].clusterId);
    } else if (
      selectedClusterId !== null &&
      !grouped.some((g) => g.clusterId === selectedClusterId)
    ) {
      setSelectedClusterId(null);
    }
  }, [grouped, selectedClusterId]);

  const selectedGroup = grouped.find((g) => g.clusterId === selectedClusterId);

  const selectedCutleryCount = selectedGroup
    ? cutleryByCluster[selectedGroup.clusterId] || 0
    : 0;

  const increase = useCallback(
    (id: string) => dispatch(increaseQuantity(id)),
    [dispatch],
  );

  const decrease = useCallback(
    (id: string) => dispatch(decreaseQuantity(id)),
    [dispatch],
  );

  const remove = useCallback(
    (id: string) => dispatch(removeFromCart(id)),
    [dispatch],
  );

  const incCutlery = useCallback(
    (clusterId: number) => dispatch(incrementCutlery(clusterId)),
    [dispatch],
  );

  const decCutlery = useCallback(
    (clusterId: number) => dispatch(decrementCutlery(clusterId)),
    [dispatch],
  );

  return {
    grouped,
    selectedClusterId,
    setSelectedClusterId,
    selectedGroup,
    selectedCutleryCount,
    isEmpty: grouped.length === 0,
    increase,
    decrease,
    remove,
    incCutlery,
    decCutlery,
  };
};
